import React, { useState, useEffect } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { useHistory, useLocation } from "react-router-dom";
import { Box, Button, Typography, styled } from "@mui/material";
import { RxCross2 } from "react-icons/rx";
import { FaPlus } from "react-icons/fa";
import { MdDelete, MdEdit } from "react-icons/md";
import heic2any from "heic2any";
import { useSelector } from "react-redux";
import FormPageHeader from "../admin/quizSystem/components/FormPageHeader";

const CircleContainer = styled("div")({
  width: 150,
  height: 150,
  borderRadius: "50%",
  border: "2px dashed #ccc",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  color: "#555",
  fontSize: "14px",
  cursor: "pointer",
  position: "relative",
  backgroundColor: "#f9f9f9",
  overflow: "hidden",
});

const ImagePreview = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "50%",
  position: "absolute",
  top: 0,
  left: 0,
});

const formPanelStyle = {
  background: "#fff",
  border: "1px solid #eef2f7",
  borderRadius: "10px",
  boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
  padding: "18px",
};

const selectMenuStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 1200 }),
  menu: (base) => ({ ...base, zIndex: 1200 }),
};

const getBusinessUnitId = (item) =>
  item?.id ?? item?.business_unit_id ?? item?.businessUnitId;

const getBusinessUnitName = (item) =>
  item?.business_unit ||
  item?.business_unit_name ||
  item?.businessUnitName ||
  item?.unit_name ||
  item?.name ||
  "";

const getLocationUnitId = (item) => item?.id 

const getLocationUnitName = (item) => item?.locations_units || "";

const normalizeBusinessUnitIds = (businessUnits) => {
  if (!Array.isArray(businessUnits)) return [];

  return businessUnits
    .map((item) =>
      typeof item === "object" && item !== null ? getBusinessUnitId(item) : item
    )
    .filter((item) => item !== undefined && item !== null && item !== "")
    .map(String);
};

const normalizeLocationUnitIds = (locations) => {
  if (!Array.isArray(locations)) return [];

  return locations
    .map((item) =>
      typeof item === "object" && item !== null ? getLocationUnitId(item) : item
    )
    .filter((item) => item !== undefined && item !== null && item !== "")
    .map(String);
};

const buildBusinessUnitOptions = (items) =>
  items
    .map((item) => {
      const id = getBusinessUnitId(item);
      if (id === undefined || id === null || id === "") return null;

      return {
        value: String(id),
        label: [`B${id}`, getBusinessUnitName(item)]
          .filter(Boolean)
          .join("-"),
        name: getBusinessUnitName(item),
      };
    })
    .filter(Boolean);

const buildLocationOptions = (items) =>
  items
    .map((item) => {
      const id = getLocationUnitId(item);
      if (id === undefined || id === null || id === "") return null;

      return {
        value: String(id),
        label: [`L${id}`, getLocationUnitName(item)]
          .filter(Boolean)
          .join("-"),
        name: getLocationUnitName(item),
      };
    })
    .filter(Boolean);

const normalizeAssignmentPairs = (pairs) =>
  pairs
    .map((pair) => ({
      business_unit: String(pair.business_unit),
      locations_units: String(pair.locations_units),
    }))
    .filter((pair) => pair.business_unit && pair.locations_units);

const toBusinessUnitLocationPayload = (pairs) =>
  pairs.map((pair) => ({
    business_unit: Number(pair.business_unit),
    locations_units: Number(pair.locations_units),
  }));

const canManageBusinessUnitAssignmentsForRole = (role) => {
  const normalizedRole = `${role || ""}`.toLowerCase().replace(/[\s_-]/g, "");

  return normalizedRole === "owner" || normalizedRole === "superadmin";
};

const isTruthyFlag = (value) => value === true;

const getNormalizedRole = (role) =>
  `${role || ""}`.toLowerCase().replace(/[\s_-]/g, "");

const filterPermissionTypes = (permissions, canViewSuperAdmin) =>
  permissions.filter((item) => {
    const normalizedType = getNormalizedRole(item.type);

    if (normalizedType === "superadmin") return canViewSuperAdmin;

    return true;
  });

const formatDateInputValue = (value) => {
  if (!value) return "";

  return String(value).split(/[T\s]/)[0];
};

const UpdateAdministrationDetail = () => {
  const jwtToken = localStorage.getItem("jwt_token");
  const { search } = useLocation();
  const history = useHistory();

  const id = new URLSearchParams(search).get("id");

  const selectedOrganization = useSelector(
    (state) => state.organization.selectedOrganization
  );
  const organisation_id =
    selectedOrganization?.organisation_id ||
    localStorage.getItem("organisation_id");
  const canManageBusinessUnitAssignments =
    canManageBusinessUnitAssignmentsForRole(selectedOrganization?.user_role) ||
    canManageBusinessUnitAssignmentsForRole(
      localStorage.getItem("user_role")
    ) ||
    canManageBusinessUnitAssignmentsForRole(localStorage.getItem("type"));

  const [peopleRoleId, setPeopleRoleId] = useState(null);

  const [userDetail, setUserDetail] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    organization_id: localStorage.getItem("organisation_id"),
    employee_id: "",
    position: "",
    awarding_state_date: "",
  });

  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [imageKey, setImageKey] = useState(Date.now());

  const [permissionTypes, setPermissionTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [businessUnitList, setBusinessUnitList] = useState([]);
  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationUnitList, setLocationUnitList] = useState([]);

  const addCacheBuster = (url) => {
    if (!url) return url;
    if (url.startsWith("data:")) return url;
    if (url.startsWith("blob:")) return url;
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}t=${imageKey}`;
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  /* IMAGE */

  const handleImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      let finalFile = file;

      if (file.type === "image/heic" || file.type === "image/heif") {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.8,
        });

        finalFile = new File(
          [convertedBlob],
          file.name.replace(/\.[^/.]+$/, ".jpeg"),
          { type: "image/jpeg" }
        );
      }

      setSelectedImage(finalFile);
      setImagePreviewUrl(URL.createObjectURL(finalFile));
      setImageKey(Date.now());
    } catch {
      toast.error("Unsupported image format.");
    }
  };

  const handleRemoveImage = (e) => {
    e.preventDefault();
    setSelectedImage(null);
    setImagePreviewUrl("");
    setImageKey(Date.now());
  };

  /* USER INPUT */

  const onChangeHandler = (e) => {
    setUserDetail({ ...userDetail, [e.target.id]: e.target.value });
  };

  /* PERMISSIONS */

  const onChangePermissionType = (type) => {
    setSelectedType(type);

    const mapped = type.permissions_list.map((perm) => ({
      name: perm,
      checked: true,
    }));

    setPermissions(mapped);
  };

  const onCheckboxHandler = (index, value) => {
    const updated = [...permissions];
    updated[index].checked = value;
    setPermissions(updated);
  };



  const fetchBusinessUnits = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/all-business-units-names/${organisation_id}`,
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await res.json();
      const list = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];

      setBusinessUnitList(list);
    } catch {
      toast.error("Business unit dropdown API error");
    }
  };

  const fetchLocationUnits = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/all-locations-units-names/${organisation_id}`,
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await res.json();
      const list = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];

      setLocationUnitList(list);
    } catch {
      toast.error("Location dropdown API error");
    }
  };

  const fetchEmployeeBusinessUnits = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/employee-business-units?organisation_id=${organisation_id}&user_id=${id}`,
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await res.json();
      const assignment = data?.data || data || {};
      const assignmentPairs = normalizeAssignmentPairs(
        assignment?.business_unit_locations ??
          data?.business_unit_locations ??
          []
      );

      if (assignmentPairs.length) {
        setSelectedBusinessUnits(Array.from(new Set(assignmentPairs.map(p => p.business_unit))));
        setSelectedLocations(Array.from(new Set(assignmentPairs.map(p => p.locations_units))));
        return;
      }

      const businessUnitIds = normalizeBusinessUnitIds(
        assignment?.business_units ?? data?.business_units
      );
      const locationIds = normalizeLocationUnitIds(
        assignment?.locations_units ?? data?.locations_units
      );

      setSelectedBusinessUnits(businessUnitIds);
      setSelectedLocations(locationIds);
    } catch {
      toast.error("Failed to load employee business units and locations");
    }
  };

  /* FETCH PERMISSION TYPES */

  const fetchOrganisationSettings = async () => {
    if (!organisation_id) return false;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/organisations/${organisation_id}`,
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await res.json();
      const organisation = data?.data?.organizations || data?.data || {};

      return isTruthyFlag(organisation.view_super_admin);
    } catch {
      return false;
    }
  };

  const fetchPermissionTypes = async (canViewSuperAdmin) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/people-permission`,
      {
        headers: { Authorization: `Bearer ${jwtToken}` },
      }
    );

    const data = await res.json();

    const perms = data.data.map((p) => ({
      ...p,
      value: p.id,
      label: p.type,
    }));

    const filteredPerms = filterPermissionTypes(perms, canViewSuperAdmin);

    setPermissionTypes(filteredPerms);

    return filteredPerms;
  };

  /* FETCH ROLE */

  const fetchRoleDetails = async (people_role_id, permList) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/update-role-data/${people_role_id}/${id}`,
        {
          headers: { Authorization: `Bearer ${jwtToken}` },
        }
      );

      const roleData = await res.json();
      const role = roleData.data;

      const type = permList.find((p) => p.type === role.type);

      setUserDetail((prev) => ({
        ...prev,
        awarding_state_date: formatDateInputValue(role.awarding_state_date),
      }));

      if (type) {
        setSelectedType(type);

        const checkedIndexes = role.checkbox
          ? role.checkbox.split(",").map(Number)
          : [];
        const mapped = type.permissions_list.map((perm, index) => ({
          name: perm,
          checked: checkedIndexes.includes(index),
        }));

        setPermissions(mapped);
      }
    } catch {
      toast.error("Failed to load role data");
    }
  };

  /* FETCH USER */

  const getAdministrationDetail = async (permList) => {
    try {
      let url = `${process.env.REACT_APP_API_URL}/employee/${id}`;

      if (organisation_id) {
        url += `?org_id=${organisation_id}`;
      }

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      const data = await res.json();

      const {
        first_name,
        last_name,
        email,
        phone_number,
        organization_id,
        employee_id,
        position,
        profile_image,
        organisation_and_role,
      } = data.data;

      setUserDetail({
        first_name,
        last_name,
        email,
        phone_number,
        organization_id,
        employee_id,
        position,
      });

      if (profile_image) {
        setImagePreviewUrl(profile_image);
        setImageKey(Date.now());
      }

      if (organisation_and_role?.length > 0) {
        const selectedOrganisationRole =
          organisation_and_role.find(
            (org) =>
              String(org.organisation_id ?? org.organization_id) ===
              String(organisation_id)
          ) || organisation_and_role[0];
        const roleId = selectedOrganisationRole.people_role_id;
        setPeopleRoleId(roleId);
        setUserDetail((prev) => ({
          ...prev,
          awarding_state_date: formatDateInputValue(
            selectedOrganisationRole.awarding_state_date
          ),
        }));

        fetchRoleDetails(roleId, permList);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const loadInitialData = async () => {
    const canViewSuperAdmin = await fetchOrganisationSettings();
    const permList = await fetchPermissionTypes(canViewSuperAdmin);
    if (canManageBusinessUnitAssignments) {
      await fetchBusinessUnits();
      await fetchLocationUnits();
      await fetchEmployeeBusinessUnits();
    }
    await getAdministrationDetail(permList);
  };

  /* SUBMIT */

  const onSubmitHandler = async (e) => {
    e.preventDefault();

    let base64Image = null;

    if (selectedImage) {
      base64Image = await handleImageToBase64(selectedImage);
    }

    const { awarding_state_date, ...submitUserDetail } = userDetail;

    const body = {
      ...submitUserDetail,
      previous_org_id: localStorage.getItem("organisation_id"),
      organization_id: localStorage.getItem("organisation_id"),
      profile_image: base64Image,
    };

    try {
      /* USER UPDATE */

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/employee/${id}`,
        {
          method: "POST",
          body: JSON.stringify(body),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );

      const result = await res.json();

      if (!res.ok) throw new Error(result.error);

      /* ROLE UPDATE */

      const checkedIndexes = permissions
        .map((p, i) => (p.checked ? i : null))
        .filter((x) => x !== null);

      const roleRes = await fetch(
        `${process.env.REACT_APP_API_URL}/update-role-data`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: +peopleRoleId,
            people_id: +id,
            organisation_id: organisation_id,
            checkbox: checkedIndexes.join(","),
            type: selectedType?.type,
            awarding_state_date:
              formatDateInputValue(awarding_state_date) || null,
          }),
        }
      );
      const roleResult = await roleRes.json();

      if (!roleRes.ok || roleResult?.error) {
        throw new Error(roleResult?.error || "Failed to save role permissions");
      }

      if (canManageBusinessUnitAssignments) {
        const businessUnitRes = await fetch(
          `${process.env.REACT_APP_API_URL}/employee-business-units`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              organisation_id: Number(organisation_id),
              user_id: Number(id),
                business_units: selectedBusinessUnits.map(Number),
                locations_units: selectedLocations.map(Number),
            }),
          }
        );
        const businessUnitResult = await businessUnitRes.json();

        if (!businessUnitRes.ok) {
          throw new Error(
            businessUnitResult.error || "Failed to save business units"
          );
        }
      }

      toast.success("User updated successfully");

      setTimeout(() => {
        history.push("/administration");
      }, 500);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="min-h-[93vh] overflow-auto bg-white p-3 text-[#20232A] sm:p-4">
      <FormPageHeader
        title="Update Administration Details"
        onBack={() => history.push("/administration")}
      />

      <form onSubmit={onSubmitHandler} style={formPanelStyle}>
        <Box
          mt={2}
          mb={2}
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Profile Photo:
          </Typography>

          <input
            accept="image/*"
            type="file"
            id="upload-image"
            style={{ display: "none" }}
            onChange={handleImageChange}
          />

          <label htmlFor="upload-image" style={{ position: "relative" }}>
            <CircleContainer>
              {imagePreviewUrl ? (
                <ImagePreview
                  key={imageKey}
                  src={addCacheBuster(imagePreviewUrl)}
                  alt="Profile Preview"
                />
              ) : (
                <span>Upload your profile photo.</span>
              )}
            </CircleContainer>

            {imagePreviewUrl && (
              <Button
                onClick={handleRemoveImage}
                sx={{
                  position: "absolute",
                  top: "10px",
                  right: "-10px",
                  backgroundColor: "#f44336",
                  color: "#fff",
                  borderRadius: "50%",
                  width: 28,
                  height: 28,
                  minWidth: "unset",
                }}
              >
                <RxCross2 size={16} />
              </Button>
            )}
          </label>
        </Box>

        <div className="row">
          {[
            "first_name",
            "last_name",
            "email",
            "phone_number",
            "employee_id",
            "position",
          ].map((field) => (
            <div className="col-md-6 mt-2" key={field}>
              <label>
                {field
                  .replace("_", " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase())}
              </label>

              <input
                type={
                  field === "email"
                    ? "email"
                    : field === "phone_number"
                    ? "number"
                    : "text"
                }
                className="form-control"
                id={field}
                value={userDetail[field]}
                onChange={onChangeHandler}
              />
            </div>
          ))}
        </div>

        <h6 className="mt-4">Update Role</h6>

        <div className="row align-items-end">
          <div className="col-md-6 mb-3">
            <label>Account Type</label>

            <Select
              options={permissionTypes}
              value={selectedType}
              onChange={onChangePermissionType}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label htmlFor="awarding_state_date">Awarding State Date</label>
            <input
              type="date"
              className="form-control"
              id="awarding_state_date"
              value={formatDateInputValue(userDetail.awarding_state_date)}
              onChange={onChangeHandler}
            />
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-md-6">
            <label>People Risk Analysis Permissions</label>
            {permissions.length > 0 ? (
              <ul className="list-unstyled">
                {permissions.map((perm, index) => (
                  <li key={index} className="mb-2 flex items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 flex-shrink-0 accent-[#1a73e8]"
                      id={`perm_${index}`}
                      checked={perm.checked}
                      onChange={(e) =>
                        onCheckboxHandler(index, e.target.checked)
                      }
                    />

                    <label
                      className="m-0 leading-5"
                      htmlFor={`perm_${index}`}
                      style={{ fontWeight: 400 }}
                    >
                      {perm.name.replace(/_/g, " ")}
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted">
                Select account type to view permissions
              </p>
            )}
          </div>

          {canManageBusinessUnitAssignments && (
            <div className="col-md-6 mt-3 border-left pl-4 md:mt-0">
              <label className="mb-2">Business Unit & Location</label>
              <div className="row">
                <div className="col-6">
                  <h6 className="text-sm font-semibold mb-2">Business Units</h6>
                  <div className="overflow-auto border rounded p-2" style={{ maxHeight: '200px' }}>
                    {businessUnitList.length > 0 ? (
                      businessUnitList.map((bu) => {
                        const id = String(getBusinessUnitId(bu));
                        const name = getBusinessUnitName(bu);
                        return (
                          <div className="mb-2 flex items-start gap-2" key={id}>
                            <input
                              className="mt-1 h-4 w-4 flex-shrink-0 accent-[#1a73e8]"
                              type="checkbox"
                              id={`bu_${id}`}
                              checked={selectedBusinessUnits.includes(id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedBusinessUnits((prev) => [...prev, id]);
                                } else {
                                  setSelectedBusinessUnits((prev) => prev.filter(x => x !== id));
                                }
                              }}
                            />
                            <label className="m-0 leading-5" htmlFor={`bu_${id}`} style={{ fontWeight: 400 }}>
                              B{id} - {name}
                            </label>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-muted text-sm">No business units available</p>
                    )}
                  </div>
                </div>

                <div className="col-6">
                  <h6 className="text-sm font-semibold mb-2">Locations</h6>
                  <div className="overflow-auto border rounded p-2" style={{ maxHeight: '200px' }}>
                    {locationUnitList.length > 0 ? (
                      locationUnitList.map((loc) => {
                        const id = String(getLocationUnitId(loc));
                        const name = getLocationUnitName(loc);
                        return (
                          <div className="mb-2 flex items-start gap-2" key={id}>
                            <input
                              className="mt-1 h-4 w-4 flex-shrink-0 accent-[#1a73e8]"
                              type="checkbox"
                              id={`loc_${id}`}
                              checked={selectedLocations.includes(id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedLocations((prev) => [...prev, id]);
                                } else {
                                  setSelectedLocations((prev) => prev.filter(x => x !== id));
                                }
                              }}
                            />
                            <label className="m-0 leading-5" htmlFor={`loc_${id}`} style={{ fontWeight: 400 }}>
                              L{id} - {name}
                            </label>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-muted text-sm">No locations available</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <button type="submit" className="btn my-3 bg_color">
          Save Changes
        </button>
      </form>
    </div>
  );
};

export default UpdateAdministrationDetail;
